import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from '@mui/material';
import {
  School,
  CheckCircle,
  Timeline,
  Assignment,
  AttachMoney,
  LibraryBooks,
  AccountBalance,
  EmojiEvents,
} from '@mui/icons-material';

const requirements = [
  'Confirmation of Acceptance for Studies (CAS)',
  'Proof of sufficient funds for tuition and living costs',
  'English language proficiency test results',
  'Valid passport',
  'Academic qualifications',
  'Tuberculosis test results (if applicable)',
];

const benefits = [
  'Study at UK\'s prestigious institutions',
  'Work part-time during studies',
  'Bring dependents (for certain courses)',
  'Post-study work opportunities',
  'Access to world-class facilities',
  'Multicultural learning environment',
];

const process = [
  {
    title: 'Initial Consultation',
    description: 'Free consultation to assess your eligibility and requirements',
  },
  {
    title: 'Document Preparation',
    description: 'Guidance on collecting and preparing necessary documents',
  },
  {
    title: 'Application Filing',
    description: 'Professional assistance with visa application submission',
  },
  {
    title: 'Application Tracking',
    description: 'Regular updates on your application status',
  },
  {
    title: 'Pre-Arrival Support',
    description: 'Guidance on preparing for your journey to the UK',
  },
];

const StudentVisa = () => {
  const theme = useTheme();

  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          mb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <School sx={{ fontSize: 64, mb: 3, color: theme.palette.secondary.light }} />
              <Typography variant="h1" gutterBottom>
                UK Student Visa Services
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
                Your gateway to world-class education in the United Kingdom
              </Typography>
              <Button
                component={RouterLink}
                to="/consultation"
                variant="contained"
                color="secondary"
                size="large"
                sx={{ mr: 2 }}
              >
                Free Consultation
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Overview Section */}
        <Grid container spacing={6} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h2" gutterBottom>
              Study in the UK
            </Typography>
            <Typography variant="body1" paragraph>
              The UK Student visa (formerly Tier 4) allows international students to study at UK's prestigious institutions. Our expert team provides comprehensive support throughout your visa application process, ensuring a smooth journey towards your educational goals.
            </Typography>
            <Typography variant="body1">
              Whether you're pursuing undergraduate, postgraduate, or research programs, we'll help you navigate the complexities of the UK's student visa system with confidence.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
                  Key Benefits
                </Typography>
                <List>
                  {benefits.map((benefit) => (
                    <ListItem key={benefit}>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Requirements Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Visa Requirements
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
                    Essential Documents
                  </Typography>
                  <List>
                    {requirements.map((requirement) => (
                      <ListItem key={requirement}>
                        <ListItemIcon>
                          <Assignment color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={requirement} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
                    Financial Requirements
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <AttachMoney color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Tuition Fees" 
                        secondary="Proof of first year's tuition fees"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <AccountBalance color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Living Costs" 
                        secondary="Minimum £1,334 per month for London (£1,023 outside London)"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Application Process */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Application Process
          </Typography>
          <Grid container spacing={3}>
            {process.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={step.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Timeline color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" component="div">
                        Step {index + 1}
                      </Typography>
                    </Box>
                    <Typography variant="h5" gutterBottom>
                      {step.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Why Choose Us */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Why Choose Us
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <EmojiEvents color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h5">
                      Expert Guidance
                    </Typography>
                  </Box>
                  <Typography variant="body1" paragraph>
                    Our team of experienced immigration lawyers specializes in UK student visas. We stay updated with the latest immigration rules and policies to provide you with accurate advice and support.
                  </Typography>
                  <Typography variant="body1">
                    We have helped thousands of students successfully obtain their UK student visas and begin their educational journey in the United Kingdom.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <LibraryBooks color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h5">
                      Comprehensive Support
                    </Typography>
                  </Box>
                  <Typography variant="body1" paragraph>
                    We provide end-to-end support throughout your visa application process, from initial consultation to post-arrival guidance.
                  </Typography>
                  <Typography variant="body1">
                    Our services include document checking, application review, interview preparation, and ongoing support to ensure your visa application success.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            py: 8,
            textAlign: 'center',
            borderRadius: 4,
            boxShadow: 3,
          }}
        >
          <Typography variant="h3" gutterBottom>
            Ready to Start Your UK Education Journey?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
            Contact us today for a free consultation and expert guidance
          </Typography>
          <Button
            component={RouterLink}
            to="/consultation"
            variant="contained"
            color="secondary"
            size="large"
            sx={{ px: 6, py: 2 }}
          >
            Book Free Consultation
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default StudentVisa; 